import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
	Box,
	List,
	ListItem,
	ListItemText,
	CircularProgress,
	Divider,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { AssetTreePicker } from "@dexteel/mesf-core";
import { moveAssetQCBD } from "@/pages/asset-details/repositories/AssetDetailsRepositorie";
import { AssetQCBD } from "@/pages/asset-details/models/AssetQCBD";

interface Props {
	show: boolean;
	selectedQCBDs: any[];
	onHide: (shouldRefresh: boolean) => void;
}

export const MoveQCBDModal = ({ show, selectedQCBDs, onHide }: Props) => {
	const [selectedAssetId, setSelectedAssetId] = useState<number>(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string>("");

	useEffect(() => {
		if (!show) {
			setSelectedAssetId(0);
			setError("");
		}
	}, [show]);

	const handleAssetSelect = (value: number | null) => {
		if (value !== null) {
			setSelectedAssetId(value);
			setError("");
		}
	};

	const handleMove = async () => {
		if (selectedQCBDs.length === 0) {
			setError("No QCBD records selected");
			return;
		}

		setLoading(true);
		setError("");

		try {
			const qcbdRecordIds = selectedQCBDs.map((qcbd) => qcbd.QCBDRecordId);
			const res = await moveAssetQCBD(qcbdRecordIds, selectedAssetId);

			if (res.ok) {
				onHide(true);
			} else {
				setError(res.message);
			}
		} catch (err: any) {
			setError(err.message || "An error occurred while moving QCBD records");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		onHide(false);
	};

	return (
		<Dialog open={show} onClose={handleCancel} maxWidth="md" fullWidth>
			<DialogTitle style={{ padding: "24px 24px 0" }}>
				<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
					MOVE QCBD
				</Typography>
			</DialogTitle>

			<DialogContent style={{ padding: "20px 24px" }}>
				<Grid2 container spacing={3}>
					<Grid2 size={{ xs: 12 }}>
						<Typography
							variant="h6"
							style={{ marginBottom: "16px", fontWeight: 500 }}
						>
							Selected QCBD Records ({selectedQCBDs.length})
						</Typography>
						<Box
							style={{
								maxHeight: "200px",
								overflowY: "auto",
								border: "1px solid #e0e0e0",
								borderRadius: "4px",
								padding: "8px",
								backgroundColor: "#fafafa",
							}}
						>
							<List dense>
								{selectedQCBDs.map((qcbd: AssetQCBD, index) => (
									<ListItem
										key={qcbd.QCBDRecordId}
										style={{ padding: "4px 8px" }}
									>
										<ListItemText
											primary={qcbd.Description}
											secondary={`File name: ${qcbd.Filename}`}
											primaryTypographyProps={{
												style: { fontSize: "14px", fontWeight: 500 },
											}}
											secondaryTypographyProps={{
												style: { fontSize: "12px", color: "#666" },
											}}
										/>
									</ListItem>
								))}
							</List>
						</Box>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Divider style={{ margin: "16px 0" }} />
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Typography
							variant="h6"
							style={{ marginBottom: "16px", fontWeight: 500 }}
						>
							Target Asset
						</Typography>
						<AssetTreePicker
							label="Asset"
							value={selectedAssetId || 0}
							onChange={handleAssetSelect}
						/>
					</Grid2>

					{error && (
						<Grid2 size={{ xs: 12 }}>
							<Typography
								color="error"
								style={{
									fontSize: "14px",
									padding: "8px 12px",
									backgroundColor: "#ffebee",
									border: "1px solid #ffcdd2",
									borderRadius: "4px",
								}}
							>
								{error}
							</Typography>
						</Grid2>
					)}
				</Grid2>
			</DialogContent>

			<DialogActions style={{ padding: "16px 24px 24px" }}>
				<Button
					onClick={handleCancel}
					variant="outlined"
					disabled={loading}
					style={{
						marginRight: "12px",
						textTransform: "none",
						fontWeight: 500,
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleMove}
					variant="contained"
					color="primary"
					disabled={loading || selectedQCBDs.length === 0}
					style={{
						textTransform: "none",
						fontWeight: 500,
						minWidth: "120px",
					}}
				>
					{loading ? (
						<Box display="flex" alignItems="center">
							<CircularProgress size={16} style={{ marginRight: "8px" }} />
							Moving...
						</Box>
					) : (
						"Move QCBD"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
